// Resource Planning view settings, remembered per person in this browser's
// localStorage: the last tab, and each tab's own settings. Only what differs
// from the defaults is stored, so a setting nobody chose follows the defaults.
// Pure apart from the storage passed in, so it can be tested.
import { DEFAULT_SETTINGS, DEFAULT_TIMEFRAME, type Settings, type TimeframeKey, type View } from './model';

export const VIEWS: readonly View[] = ['people', 'projects', 'timeline'];
const TIMEFRAMES: readonly TimeframeKey[] = ['thisMonth', 'lastMonth', 'next3', 'next6', 'next12', 'thisYear'];
const PERSON_GROUPS = ['role', 'none'] as const;
const PERSON_SORTS = ['name', 'avg', 'peak', 'free'] as const;
const PROJECT_GROUPS = ['status', 'none'] as const;
const PROJECT_SORTS = ['start', 'name', 'hours', 'unstaffed'] as const;
const MONTH = /^\d{4}-\d{2}$/;
/** 2: only changed settings stored. 1 stored every setting, so its grouping was the old default. */
const VERSION = 2;

/** One tab's settings. */
export interface TabPrefs {
  settings: Settings;
  timeframe: TimeframeKey;
  collapsed: string[];
  expanded: string[];
}

export interface ViewPrefs {
  view: View;
  tabs: Record<View, TabPrefs>;
}

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'>;

export function prefsKey(userId: string): string {
  return `mothership.resourcing.user.${userId}`;
}

export function defaultTab(view: View): TabPrefs {
  return { settings: { ...DEFAULT_SETTINGS, show: { ...DEFAULT_SETTINGS.show }, view }, timeframe: DEFAULT_TIMEFRAME, collapsed: [], expanded: [] };
}

export function defaultPrefs(): ViewPrefs {
  return { view: DEFAULT_SETTINGS.view, tabs: { people: defaultTab('people'), projects: defaultTab('projects'), timeline: defaultTab('timeline') } };
}

const oneOf = <T extends string>(options: readonly T[], value: unknown, fallback: T): T =>
  options.includes(value as T) ? (value as T) : fallback;
const bool = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback);
const keys = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((k): k is string => typeof k === 'string').slice(0, 500) : [];
const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/** A stored tab, with anything missing or unknown replaced by its default. */
export function parseTab(view: View, raw: unknown, version = VERSION): TabPrefs {
  const d = defaultTab(view);
  const t = record(raw);
  const s = { ...record(t.settings) };
  if (version < 2) {
    delete s.personGroup;
    delete s.projectGroup;
  }
  const show = record(s.show);
  return {
    settings: {
      view,
      personGroup: oneOf(PERSON_GROUPS, s.personGroup, d.settings.personGroup),
      personSort: oneOf(PERSON_SORTS, s.personSort, d.settings.personSort),
      projectGroup: oneOf(PROJECT_GROUPS, s.projectGroup, d.settings.projectGroup),
      projectSort: oneOf(PROJECT_SORTS, s.projectSort, d.settings.projectSort),
      show: {
        confirmed: bool(show.confirmed, true),
        assigned: bool(show.assigned, true),
        forecast: bool(show.forecast, true),
      },
      weighted: bool(s.weighted, d.settings.weighted),
      focus: typeof s.focus === 'string' && MONTH.test(s.focus) ? s.focus : null,
    },
    timeframe: oneOf(TIMEFRAMES, t.timeframe, d.timeframe),
    collapsed: keys(t.collapsed),
    expanded: keys(t.expanded),
  };
}

export function parsePrefs(raw: string | null): ViewPrefs {
  let value: unknown = null;
  try {
    value = raw === null ? null : JSON.parse(raw);
  } catch {
    value = null;
  }
  const p = record(value);
  const tabs = record(p.tabs);
  const version = typeof p.v === 'number' ? p.v : 1;
  return {
    view: oneOf(VIEWS, p.view, DEFAULT_SETTINGS.view),
    tabs: {
      people: parseTab('people', tabs.people, version),
      projects: parseTab('projects', tabs.projects, version),
      timeline: parseTab('timeline', tabs.timeline, version),
    },
  };
}

/** A tab as stored: only what differs from its defaults. */
export function sparseTab(tab: TabPrefs): Record<string, unknown> {
  const d = defaultTab(tab.settings.view);
  const settings: Record<string, unknown> = {};
  for (const key of Object.keys(tab.settings) as (keyof Settings)[]) {
    if (key !== 'view' && JSON.stringify(tab.settings[key]) !== JSON.stringify(d.settings[key])) settings[key] = tab.settings[key];
  }
  const out: Record<string, unknown> = {};
  if (Object.keys(settings).length) out.settings = settings;
  if (tab.timeframe !== d.timeframe) out.timeframe = tab.timeframe;
  if (tab.collapsed.length) out.collapsed = tab.collapsed;
  if (tab.expanded.length) out.expanded = tab.expanded;
  return out;
}

function browserStorage(): KeyValueStore | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** This person's settings, or the defaults when nothing valid is stored or storage is blocked. */
export function loadPrefs(userId: string, storage: KeyValueStore | undefined = browserStorage()): ViewPrefs {
  try {
    return parsePrefs(storage?.getItem(prefsKey(userId)) ?? null);
  } catch {
    return defaultPrefs();
  }
}

/** Remembers `tab` as the selected tab with these settings. */
export function saveTab(userId: string, tab: TabPrefs, storage: KeyValueStore | undefined = browserStorage()): void {
  try {
    const prefs = loadPrefs(userId, storage);
    const view = tab.settings.view;
    const tabs = { ...prefs.tabs, [view]: tab };
    storage?.setItem(prefsKey(userId), JSON.stringify({
      v: VERSION, view, tabs: Object.fromEntries(VIEWS.map((v) => [v, sparseTab(tabs[v])])),
    }));
  } catch {
    // Private windows and full storage keep the defaults.
  }
}
